/*
  Review carousel (sections/nr-customer-reviews.liquid).
  The arrows move one card at a time and wrap round at either end. While the
  carousel is on screen it moves on by itself every 5 seconds, and stops while
  the shopper hovers, focuses or touches it, when the tab is hidden, or for
  people who prefer reduced motion. Arrows hide when every card already fits.
*/
if (!customElements.get('nr-customer-reviews')) {
  customElements.define(
    'nr-customer-reviews',
    class NrCustomerReviews extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        if (!this.track || !this.prev || !this.next) return;

        this.delay = 5000;
        this.edge = 12;
        this.visible = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.prev.addEventListener('click', () => {
          this.go(-1);
          this.schedule();
        });
        this.next.addEventListener('click', () => {
          this.go(1);
          this.schedule();
        });

        const stop = () => this.stop();
        const start = () => this.schedule();
        this.addEventListener('mouseenter', stop);
        this.addEventListener('mouseleave', start);
        this.addEventListener('focusin', stop);
        this.addEventListener('focusout', start);
        this.addEventListener('pointerdown', stop, { passive: true });

        this.onVisibility = () => (document.hidden ? this.stop() : this.schedule());
        document.addEventListener('visibilitychange', this.onVisibility);

        this.onResize = () => {
          cancelAnimationFrame(this.resizeFrame);
          this.resizeFrame = requestAnimationFrame(() => {
            this.measure();
            this.schedule();
          });
        };
        window.addEventListener('resize', this.onResize, { passive: true });

        this.observer = new IntersectionObserver(
          (entries) => {
            this.visible = entries.some((entry) => entry.isIntersecting);
            if (this.visible) {
              this.measure();
              this.schedule();
            } else {
              this.stop();
            }
          },
          { rootMargin: '200px 0px' }
        );
        this.observer.observe(this);
        this.measure();
      }

      disconnectedCallback() {
        this.stop();
        if (this.observer) this.observer.disconnect();
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      }

      measure() {
        const card = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.step = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
        this.maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        this.overflows = this.track.scrollWidth > this.track.clientWidth + 2;

        [this.prev, this.next].forEach((button) => {
          button.style.visibility = this.overflows ? 'visible' : 'hidden';
          button.disabled = !this.overflows;
        });
      }

      go(direction) {
        if (!this.overflows) return;
        const left = this.track.scrollLeft;

        // Past either end, jump round to the other.
        if (direction > 0 && left >= this.maxScroll - this.edge) {
          this.track.scrollTo({ left: 0, behavior: 'auto' });
        } else if (direction < 0 && left <= this.edge) {
          const last = this.step > 0 ? Math.floor(this.maxScroll / this.step) * this.step : this.maxScroll;
          this.track.scrollTo({ left: last, behavior: 'auto' });
        } else {
          this.track.scrollBy({ left: direction * (this.step || this.track.clientWidth), behavior: 'smooth' });
        }
      }

      stop() {
        clearTimeout(this.timer);
        this.timer = null;
      }

      schedule() {
        this.stop();
        if (this.reducedMotion || !this.overflows || !this.visible || document.hidden) return;
        this.timer = setTimeout(() => {
          this.go(1);
          this.schedule();
        }, this.delay);
      }
    }
  );
}
